import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * Eterna — buy-box extras: instant engraving (The Crown) + the gift box.
 * 1. PDP blocks: engraving = live textPath preview + text input; gift box = checkbox row.
 *    When either extra is active, this module owns the product-form submit and performs
 *    an atomic batch add (racket + service line(s)) in one /cart/add.js request.
 * 2. Cart guardian (all pages): keeps racket/fee pairs consistent — removes orphaned fees,
 *    matches fee quantity to racket quantity, restores a deleted fee line.
 * Engraving pairs link via `_pers_id` (fee carries `_service: engraving`);
 * gift box pairs link via `_gift_id` (fee carries `_service: giftbox`);
 * white cover pairs link via `_cover_id` (fee carries `_service: cover`, 21.10.2026 test).
 */

const GUARDIAN_SOURCE = 'et-engraving-guardian';
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
// property key the gift box writes on the racket line; the engraving guardian skips it
// when recovering the engraving text from a racket's visible properties
const GIFT_PROPERTY_KEY = 'Gift box';
// visible message property the PDP gift block writes on the racket line (17.09.2026)
const GIFT_MESSAGE_KEY = 'Gift message';
// visible property the cover choice writes on the racket line (21.10.2026, test themes);
// absent = the black cover that comes with every racket
const COVER_PROPERTY_KEY = 'Cover';

/* ---------------- PDP blocks ---------------- */

let submitBound = false;
let addInFlight = false;

// formId -> { el, attr } — the theme's [on:submit] delegation target for that form.
// Captured once so ownership can be handed back when every extra goes inactive.
const themeSubmitTargets = new Map();

function engravingRoot(formId) {
  for (const root of document.querySelectorAll('[data-et-engraving]')) {
    if (root.dataset.formId === formId) return root;
  }
  return null;
}

function giftboxRoot(formId) {
  for (const root of document.querySelectorAll('[data-et-giftbox]')) {
    if (root.dataset.formId === formId) return root;
  }
  return null;
}

function coverRoot(formId) {
  for (const root of document.querySelectorAll('[data-et-cover]')) {
    if (root.dataset.formId === formId) return root;
  }
  return null;
}

function extrasState(formId) {
  const eng = engravingRoot(formId);
  const engInput = eng ? eng.querySelector('[data-et-eng-input]') : null;
  const engText = engInput ? engInput.value.trim() : '';
  const gbx = giftboxRoot(formId);
  const gbxInput = gbx ? gbx.querySelector('[data-et-gbx-input]') : null;
  const gbxChecked = !!(gbxInput && gbxInput.checked);
  const cov = coverRoot(formId);
  const covInput = cov ? cov.querySelector('[data-et-cover-input]:checked') : null;
  const coverWhite = !!(covInput && covInput.value === 'white' && cov.dataset.feeVariant);
  return { eng, engInput, engText, gbx, gbxChecked, cov, coverWhite, active: !!engText || gbxChecked || coverWhite };
}

// Horizon delegates `submit` via an earlier document-capture listener that resolves
// the handler from the closest [on:submit] ancestor (product-form-component) at event
// time. While an extra is active we own the submit (atomic batch add), so detach
// that attribute; restore it the moment every extra is inactive again.
function syncSubmitOwnership(formId) {
  const form = document.getElementById(formId);
  if (!form) return;

  let target = themeSubmitTargets.get(formId);
  // a section re-render (theme editor) replaces the component; a cached
  // detached node would absorb the attribute toggles while the live one
  // keeps its server-rendered on:submit — re-resolve in that case
  if (target && !target.el.isConnected) target = null;
  if (!target) {
    const el = form.closest('[on\\:submit]');
    if (!el) return;
    target = { el, attr: el.getAttribute('on:submit') };
    themeSubmitTargets.set(formId, target);
  }

  if (extrasState(formId).active) target.el.removeAttribute('on:submit');
  else target.el.setAttribute('on:submit', target.attr);
}

/* ---------------- already-in-cart retro apply ---------------- */

let cartSnapshot = { at: 0, data: null };

async function getCartCached() {
  if (cartSnapshot.data && Date.now() - cartSnapshot.at < 2500) return cartSnapshot.data;
  const cart = await fetchCart();
  cartSnapshot = { at: Date.now(), data: cart };
  return cart;
}

function invalidateCartSnapshot() {
  cartSnapshot = { at: 0, data: null };
}

function currentVariantId(formId) {
  const form = document.getElementById(formId);
  if (!form) return 0;
  return Number(new FormData(form).get('id')) || 0;
}

// a line this extra can retro-attach to: same variant, not a service line,
// not already carrying this extra's pair id
function eligibleLine(cart, variantId, pairKey) {
  if (!cart || !Array.isArray(cart.items) || !variantId) return null;
  return (
    cart.items.find(
      (item) =>
        item.id === variantId &&
        !(item.properties && (item.properties._service || item.properties[pairKey]))
    ) || null
  );
}

async function changeLineProperties(line, extraProps) {
  const response = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      id: line.key,
      quantity: line.quantity,
      properties: { ...(line.properties || {}), ...extraProps },
    }),
  });
  return response.ok;
}

const hintEvaluators = [];

function wireInCartHint(root, { pairKey, isActive, apply }) {
  const hint = root.querySelector('[data-et-incart]');
  const applyBtn = hint ? hint.querySelector('[data-et-incart-apply]') : null;
  const doneEl = root.querySelector('[data-et-incart-done]');
  if (!hint || !applyBtn) return () => {};

  let token = 0;
  const evaluate = async () => {
    if (!isActive()) {
      hint.hidden = true;
      return;
    }
    const my = ++token;
    const cart = await getCartCached();
    if (my !== token) return;
    if (!isActive()) {
      hint.hidden = true;
      return;
    }
    hint.hidden = !eligibleLine(cart, currentVariantId(root.dataset.formId), pairKey);
  };

  let applying = false;
  applyBtn.addEventListener('click', async () => {
    if (applying) return;
    applying = true;
    try {
      const cart = await fetchCart();
      const line = eligibleLine(cart, currentVariantId(root.dataset.formId), pairKey);
      if (!line) {
        hint.hidden = true;
        return;
      }
      const ok = await apply(line);
      if (!ok) return;
      invalidateCartSnapshot();
      hint.hidden = true;
      if (doneEl) {
        doneEl.hidden = false;
        setTimeout(() => {
          doneEl.hidden = true;
        }, 6000);
      }
      // pairs the fee line; the trailing scheduled pass retries once with a
      // fresh blacklist if the first add was rejected (e.g. rate limited)
      Promise.resolve(reconcile())
        .catch(() => {})
        .finally(() => scheduleReconcile());
    } catch (error) {
      console.error('[et-engraving] retro apply failed', error);
    } finally {
      applying = false;
    }
  });

  hintEvaluators.push(evaluate);
  return evaluate;
}

function initBlocks(scope) {
  const engRoots = (scope || document).querySelectorAll('[data-et-engraving]');
  for (const root of engRoots) {
    if (root.dataset.etEngInit) continue;
    root.dataset.etEngInit = '1';

    const input = root.querySelector('[data-et-eng-input]');
    const textEl = root.querySelector('[data-et-eng-text]');
    const textPath = textEl ? textEl.querySelector('textPath') : null;
    if (!input || !textPath) continue;

    const idleText = root.dataset.previewDefault || '';
    const syncPreview = () => {
      const value = input.value.trim();
      textPath.textContent = value || idleText;
      textEl.setAttribute('fill-opacity', value ? '0.95' : '0.28');
      // keep the input out of the form while empty so a blank
      // "Engraving" property never reaches the order
      if (value) input.setAttribute('form', root.dataset.formId);
      else input.removeAttribute('form');
      syncSubmitOwnership(root.dataset.formId);
    };
    input.addEventListener('input', syncPreview);
    syncPreview();

    const evaluateHint = wireInCartHint(root, {
      pairKey: '_pers_id',
      isActive: () => !!input.value.trim(),
      apply: async (line) => {
        const text = input.value.trim();
        if (!text) return false;
        if (input.pattern) {
          const re = new RegExp(`^(?:${input.pattern})$`);
          if (!re.test(text)) {
            const errorEl = root.querySelector('[data-et-eng-error]');
            if (errorEl) {
              errorEl.textContent = input.title || 'Latin letters, numerals and simple punctuation.';
              errorEl.hidden = false;
            }
            return false;
          }
        }
        const persId = `crown-${line.id}-${Date.now().toString(36)}`;
        const ok = await changeLineProperties(line, {
          [root.dataset.propertyKey || 'Engraving — The Crown']: text,
          _pers_id: persId,
          _fee_variant: root.dataset.feeVariant,
        });
        if (ok) {
          input.value = '';
          syncPreview();
        }
        return ok;
      },
    });
    let hintTimer = null;
    input.addEventListener('input', () => {
      if (hintTimer) clearTimeout(hintTimer);
      hintTimer = setTimeout(evaluateHint, 350);
    });
  }

  const gbxRoots = (scope || document).querySelectorAll('[data-et-giftbox]');
  for (const root of gbxRoots) {
    if (root.dataset.etGbxInit) continue;
    root.dataset.etGbxInit = '1';

    const input = root.querySelector('[data-et-gbx-input]');
    if (!input) continue;

    const stateEl = root.querySelector('[data-et-gbx-state]');
    const priceText = stateEl ? stateEl.textContent : '';
    const syncChoice = () => {
      if (stateEl) stateEl.textContent = input.checked ? root.dataset.addedLabel || priceText : priceText;
      syncSubmitOwnership(root.dataset.formId);
    };
    input.addEventListener('change', syncChoice);
    syncChoice();

    const evaluateHint = wireInCartHint(root, {
      pairKey: '_gift_id',
      isActive: () => input.checked,
      apply: async (line) => {
        const giftId = `gift-${line.id}-${Date.now().toString(36)}`;
        const ok = await changeLineProperties(line, {
          [root.dataset.propertyKey || GIFT_PROPERTY_KEY]: root.dataset.propertyValue || 'Included',
          _gift_id: giftId,
          _gift_variant: root.dataset.feeVariant,
        });
        if (ok) {
          input.checked = false;
          syncChoice();
        }
        return ok;
      },
    });
    input.addEventListener('change', evaluateHint);
  }

  const covRoots = (scope || document).querySelectorAll('[data-et-cover]');
  for (const root of covRoots) {
    if (root.dataset.etCovInit) continue;
    root.dataset.etCovInit = '1';
    // the same racket already in the cart with Classic Black → offer to switch that racket's cover
    const evaluateHint = wireInCartHint(root, {
      pairKey: '_cover_id',
      isActive: () => {
        const checked = root.querySelector('[data-et-cover-input]:checked');
        return !!(checked && checked.value === 'white');
      },
      apply: async (line) => {
        const ok = await attachCoverToLine(line, root.dataset.feeVariant, root.dataset.propertyKey, root.dataset.propertyValue);
        if (!ok) return false;
        const black = root.querySelector('[data-et-cover-input][value="black"]');
        if (black) {
          black.checked = true;
          black.dispatchEvent(new Event('change', { bubbles: true }));
        }
        await announceRetroAdd(root.dataset.formId, root.dataset.productId);
        return true;
      },
    });
    root.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.matches('[data-et-cover-input]')) return;
      syncSubmitOwnership(root.dataset.formId);
      refreshPdpSets();
      evaluateHint();
    });
    syncSubmitOwnership(root.dataset.formId);
  }

  if (!submitBound) {
    submitBound = true;
    document.addEventListener('submit', onSubmitCapture, { capture: true });
  }
}

function onSubmitCapture(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const formId = form.getAttribute('id');
  if (!formId) return;
  const state = extrasState(formId);
  if (!state.active) return; // no extras — let the theme's product form handle it

  if (state.engText && state.engInput && state.engInput.pattern) {
    const re = new RegExp(`^(?:${state.engInput.pattern})$`);
    if (!re.test(state.engText)) return; // let native validation surface the message
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  batchAdd(form, state);
}

// extra (PDP gift block, 17.09.2026): { message, messageKey, source, errorEl }
async function batchAdd(form, state, extra = {}) {
  if (addInFlight) return;
  addInFlight = true;

  // error surface of every ACTIVE extra — a gift-box-only failure must not
  // land in the (possibly absent or collapsed) engraving block
  const errorEls = [];
  if (state.engText && state.eng) {
    const el = state.eng.querySelector('[data-et-eng-error]');
    if (el) errorEls.push(el);
  }
  if (state.gbxChecked && state.gbx) {
    const el = state.gbx.querySelector('[data-et-gbx-error]');
    if (el) errorEls.push(el);
  }
  if (state.coverWhite && state.cov) {
    const el = state.cov.querySelector('[data-et-cover-error]');
    if (el) errorEls.push(el);
  }
  if (extra.errorEl) errorEls.push(extra.errorEl);
  const showError = (message) => {
    for (const el of errorEls) {
      el.textContent = message;
      el.hidden = false;
      const details = el.closest('details');
      if (details) details.open = true;
    }
  };
  for (const el of errorEls) el.hidden = true;

  try {
    const formData = new FormData(form);
    const racketId = Number(formData.get('id'));
    const quantity = Number(formData.get('quantity')) || 1;
    const stamp = Date.now().toString(36);

    const racketProperties = {};
    const items = [{ id: racketId, quantity, properties: racketProperties }];

    if (state.engText && state.eng && state.eng.dataset.feeVariant) {
      const feeId = Number(state.eng.dataset.feeVariant);
      const persId = `crown-${racketId}-${stamp}`;
      const propertyKey = state.eng.dataset.propertyKey || 'Engraving — The Crown';
      racketProperties[propertyKey] = state.engText;
      racketProperties._pers_id = persId;
      racketProperties._fee_variant = String(feeId);
      if (extra.source) racketProperties._pers_source = extra.source;
      items.push({
        id: feeId,
        quantity,
        properties: {
          Text: state.engText,
          For: state.eng.dataset.productTitle || '',
          _service: 'engraving',
          _pers_id: persId,
        },
      });
    }

    if (state.gbxChecked && state.gbx && state.gbx.dataset.feeVariant) {
      const feeId = Number(state.gbx.dataset.feeVariant);
      const giftId = `gift-${racketId}-${stamp}`;
      const propertyKey = state.gbx.dataset.propertyKey || GIFT_PROPERTY_KEY;
      racketProperties[propertyKey] = state.gbx.dataset.propertyValue || 'Included';
      racketProperties._gift_id = giftId;
      racketProperties._gift_variant = String(feeId);
      if (extra.message) racketProperties[extra.messageKey || GIFT_MESSAGE_KEY] = extra.message;
      if (extra.source) racketProperties._gift_source = extra.source;
      const feeProperties = {
        For: state.gbx.dataset.productTitle || '',
        _service: 'giftbox',
        _gift_id: giftId,
      };
      if (extra.message) feeProperties.Message = extra.message;
      items.push({
        id: feeId,
        quantity,
        properties: feeProperties,
      });
    }

    if (state.coverWhite && state.cov && state.cov.dataset.feeVariant) {
      const feeId = Number(state.cov.dataset.feeVariant);
      const coverId = `cover-${racketId}-${stamp}`;
      racketProperties[state.cov.dataset.propertyKey || COVER_PROPERTY_KEY] = state.cov.dataset.propertyValue || 'Club White';
      racketProperties._cover_id = coverId;
      racketProperties._cover_variant = String(feeId);
      items.push({
        id: feeId,
        quantity,
        properties: {
          For: state.cov.dataset.productTitle || '',
          _service: 'cover',
          _cover_id: coverId,
        },
      });
    }

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    // the cart lists the newest line first: send the racket last so it sits on top
    // with its service lines right below it
    const payload = { items: [...items.slice(1), items[0]], sections: sectionIds.join(',') };

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    if (data.status) {
      showError(data.description || data.message || 'Could not add to cart.');
      return;
    }

    const cart = await fetchCart();
    const sourceRoot = state.eng || state.gbx || state.cov;
    document.dispatchEvent(
      new CartAddEvent(cart ?? undefined, sourceRoot ? sourceRoot.dataset.formId : '', {
        source: 'product-form-component',
        itemCount: quantity * items.length,
        productId: sourceRoot ? sourceRoot.dataset.productId : undefined,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('[et-engraving] add failed', error);
    showError('Could not add to cart. Please try again.');
  } finally {
    addInFlight = false;
  }
}

/* ---------------- Cart guardian ---------------- */

let reconcileTimer = null;
let reconciling = false;

/* cart busy (22.10.2026, test): while the guardian re-pairs services with their rackets the cart can
   briefly show e.g. two rackets with one cover. html.et-cart-busy shows "Updating cart…" next to the
   checkout button (snippets/cart-summary.liquid) and takes the checkout and wallet buttons out of play. */
let cartBusy = false;
let cartBusyTimer = null;

function applyCartBusy() {
  document.documentElement.classList.toggle('et-cart-busy', cartBusy);
  for (const el of document.querySelectorAll('.cart__ctas .additional-checkout-buttons')) {
    if (cartBusy) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }
}

function setCartBusy(on) {
  cartBusy = on;
  if (cartBusyTimer) clearTimeout(cartBusyTimer);
  // never leave the checkout blocked if an update gets lost
  cartBusyTimer = on
    ? setTimeout(() => {
        cartBusy = false;
        applyCartBusy();
      }, 10000)
    : null;
  applyCartBusy();
}

function cartIdle() {
  return !reconcileTimer && !reconciling && !coverSwitchBusy;
}

function settleCartBusy() {
  if (cartBusy && cartIdle()) setCartBusy(false);
}

async function waitCartIdle(limitMs) {
  const until = Date.now() + limitMs;
  while ((!cartIdle() || cartBusy) && Date.now() < until) {
    await new Promise((resolve) => setTimeout(resolve, 120));
  }
}

async function fetchCart() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return await response.json();
  } catch {
    return null;
  }
}

// Engraving fee lines carry a visible Text property recovered from the racket line:
// first visible (non-underscore) property that isn't the gift box marker.
function engravingTextFrom(racket) {
  for (const [key, value] of Object.entries(racket.properties)) {
    // the studio also writes the lettering style and the logo file links as visible properties
    if (!key.startsWith('_') && key !== GIFT_PROPERTY_KEY && key !== GIFT_MESSAGE_KEY && key !== COVER_PROPERTY_KEY && !key.startsWith('Logo — ') && key !== 'Engraving — Font' && value) return value;
  }
  return '';
}

// engraving-family services share one shape; textKey names the racket-line property
// that carries this zone's text (fallback: first visible property, legacy crown lines)
function engravingServiceConfig(service, pairKey, variantKey, textKey) {
  return {
    service,
    pairKey,
    variantKey,
    restoreProperties: (racket, pid) => ({
      Text: (textKey && racket.properties[textKey]) || engravingTextFrom(racket),
      For: racket.product_title || '',
      _service: service,
      [pairKey]: pid,
    }),
  };
}

const SERVICES = [
  engravingServiceConfig('engraving', '_pers_id', '_fee_variant', 'Engraving — The Crown'),
  engravingServiceConfig('engraving-signature', '_sig_id', '_sig_variant', 'Engraving — The Signature'),
  engravingServiceConfig('engraving-wing-left', '_wl_id', '_wl_variant', 'Engraving — The Wing, left'),
  engravingServiceConfig('engraving-wing-right', '_wr_id', '_wr_variant', 'Engraving — The Wing, right'),
  {
    service: 'giftbox',
    pairKey: '_gift_id',
    variantKey: '_gift_variant',
    restoreProperties: (racket, pid) => ({
      For: racket.product_title || '',
      _service: 'giftbox',
      _gift_id: pid,
    }),
  },
  // 01.10.2026: The Logo (personalisation studio) — the visitor's artwork; the file itself lives on the racket line
  {
    service: 'logo',
    pairKey: '_logo_id',
    variantKey: '_logo_variant',
    restoreProperties: (racket, pid) => ({
      For: racket.product_title || '',
      Placement: (racket.properties && racket.properties['Logo — Placement']) || '',
      _service: 'logo',
      _logo_id: pid,
    }),
  },
  // 21.10.2026 (test themes): the white cover in place of the black one included with every racket
  {
    service: 'cover',
    pairKey: '_cover_id',
    variantKey: '_cover_variant',
    restoreProperties: (racket, pid) => ({
      For: racket.product_title || '',
      _service: 'cover',
      _cover_id: pid,
    }),
  },
];

function fixSignature(fix) {
  return `${fix.type}:${fix.key || fix.id}:${fix.quantity}`;
}

/**
 * Returns the single next fix to apply, or null when the cart is consistent.
 * One fix per pass — line keys shift after every change, so we re-fetch between fixes.
 * Fixes whose signature is in `failed` (rejected by Shopify earlier in this run)
 * are skipped so one broken pair cannot starve the others.
 */
function findFix(cart, failed) {
  if (!cart || !Array.isArray(cart.items)) return null;

  for (const cfg of SERVICES) {
    const rackets = new Map();
    const fees = new Map();
    for (const item of cart.items) {
      const pid = item.properties && item.properties[cfg.pairKey];
      if (!pid) continue;
      if (item.properties._service === cfg.service) fees.set(pid, item);
      else if (!item.properties._service) rackets.set(pid, item);
    }

    for (const [pid, fee] of fees) {
      const racket = rackets.get(pid);
      let fix = null;
      if (!racket) fix = { type: 'change', key: fee.key, quantity: 0 };
      else if (fee.quantity !== racket.quantity) fix = { type: 'change', key: fee.key, quantity: racket.quantity };
      if (fix && !(failed && failed.has(fixSignature(fix)))) return fix;
    }

    for (const [pid, racket] of rackets) {
      if (fees.has(pid)) continue;
      const feeVariant = Number(racket.properties[cfg.variantKey]);
      if (!feeVariant) continue;
      const fix = {
        type: 'add',
        id: feeVariant,
        quantity: racket.quantity,
        properties: cfg.restoreProperties(racket, pid),
      };
      if (!(failed && failed.has(fixSignature(fix)))) return fix;
    }
  }

  return null;
}

async function applyFix(fix) {
  if (fix.type === 'change') {
    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: fix.key, quantity: fix.quantity }),
    });
    return response.ok;
  }
  if (fix.type === 'add') {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: fix.id, quantity: fix.quantity, properties: fix.properties }] }),
    });
    if (!response.ok) return false;
    const data = await response.json().catch(() => null);
    return !(data && data.status);
  }
  return false;
}

async function reconcile() {
  if (reconciling) return;
  reconciling = true;
  let changed = false;
  let capped = true; // true when the loop consumed every pass with fixes still pending
  const failed = new Set();

  try {
    for (let pass = 0; pass < 6; pass++) {
      const cart = await fetchCart();
      const fix = findFix(cart, failed);
      if (!fix) {
        capped = false;
        break;
      }
      if (await applyFix(fix)) changed = true;
      else failed.add(fixSignature(fix));
    }
    if (changed) {
      const cart = await fetchCart();
      if (cart) {
        document.dispatchEvent(
          new CartUpdateEvent(cart, GUARDIAN_SOURCE, {
            source: GUARDIAN_SOURCE,
            itemCount: cart.item_count,
          })
        );
      }
    }
    // ran out of passes with work left (not merely blacklisted failures) — go again
    if (capped) scheduleReconcile();
  } catch (error) {
    console.error('[et-engraving] reconcile failed', error);
  } finally {
    reconciling = false;
    settleCartBusy();
  }
}

function scheduleReconcile() {
  if (reconcileTimer) clearTimeout(reconcileTimer);
  reconcileTimer = setTimeout(() => {
    reconcileTimer = null;
    reconcile();
  }, 300);
}

// A checkout click inside the guardian's debounce window could carry a
// desynced cart into checkout (client-side reconciliation only). Flush any
// pending pass first, then resubmit; checkout is never left blocked.
let checkoutResubmitting = false;
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    if (checkoutResubmitting) {
      checkoutResubmitting = false;
      return;
    }
    if (cartIdle() && !cartBusy) return; // nothing pending — let it through
    event.preventDefault();
    event.stopImmediatePropagation();
    if (reconcileTimer) {
      clearTimeout(reconcileTimer);
      reconcileTimer = null;
    }
    Promise.resolve(reconcile())
      .catch(() => {})
      .then(() => waitCartIdle(8000))
      .finally(() => {
        checkoutResubmitting = true;
        try {
          form.requestSubmit(submitter);
        } catch {
          submitter.click();
        }
      });
  },
  { capture: true }
);

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  invalidateCartSnapshot();
  for (const evaluate of hintEvaluators) evaluate();
  const source = event.detail && event.detail.data && event.detail.data.source;
  if (source === GUARDIAN_SOURCE) {
    applyCartBusy();
    return;
  }
  if (!(event instanceof CartAddEvent)) setCartBusy(true);
  scheduleReconcile();
});

// a quantity / remove control in the cart: busy from the click, before the theme's request returns
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('cart-items-component')) return;
    if (target.closest('.quantity-minus, .quantity-plus, .cart-items__remove')) setCartBusy(true);
  },
  { capture: true }
);
document.addEventListener(
  'change',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target && target.closest('cart-items-component') && target.matches('input[name="updates[]"]')) setCartBusy(true);
  },
  { capture: true }
);

/* ---------------- cart feedback: open the drawer on genuine adds ---------------- */
// Horizon's stock auto_open_cart_drawer flag reacts to EVERY cart:update
// (CartAddEvent and CartUpdateEvent share one event name), so it would also
// pop the drawer on cart-page quantity edits, failed adds and our guardian
// fixes. The theme flag stays OFF; we open the drawer ourselves, only on a
// successful addition.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (!(event instanceof CartAddEvent)) return;
  if (event.detail && event.detail.data && event.detail.data.didError) return;
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
});

/* ---------------- PDP set: engraving + gift box, shared by the two blocks (17.09.2026) ---------------- */
// Blocks: sections/et-pdp-personalisation.liquid ([data-et-eng-block], own item = the Crown engraving)
// and sections/et-pdp-gift-box.liquid ([data-et-gift-block], own item = the gift box). Each block's
// button adds the racket with its own item plus whatever else the visitor has chosen on the page:
// an engraving text typed in either block or in the buy-box row, the gift box ticked in the buy-box
// or a message typed in the gift block. Both blocks render the same summary (racket · engraving ·
// gift box · total) and their button labels say exactly what goes to the cart. The add itself is
// batchAdd (racket + fee lines in one request); a racket already in the cart gets the missing
// extras attached to its line instead (same retro path as the buy-box hints).

function giftBlockForm() {
  const gbx = document.querySelector('[data-et-giftbox]');
  if (gbx && gbx.dataset.formId) {
    const form = document.getElementById(gbx.dataset.formId);
    if (form) return form;
  }
  const eng = document.querySelector('[data-et-engraving]');
  if (eng && eng.dataset.formId) {
    const form = document.getElementById(eng.dataset.formId);
    if (form) return form;
  }
  return document.querySelector('form[id^="BuyButtons-ProductForm-"]') || document.querySelector('product-form-component form');
}

async function renderedCartSections() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  if (!ids.length) return undefined;
  try {
    const response = await fetch(`${Theme.routes.cart_url || '/cart'}?sections=${ids.join(',')}`, {
      headers: { Accept: 'application/json' },
    });
    return response.ok ? await response.json() : undefined;
  } catch {
    return undefined;
  }
}

// after a retro attach (line properties changed, guardian added the fee lines):
// announce it like a genuine add so the cart drawer opens with fresh sections
async function announceRetroAdd(formId, productId) {
  const [freshCart, sections] = await Promise.all([fetchCart(), renderedCartSections()]);
  document.dispatchEvent(
    new CartAddEvent(freshCart ?? undefined, formId, {
      source: 'product-form-component',
      itemCount: 1,
      productId,
      sections,
    })
  );
}

const money = (cents) => {
  const n = Math.round(Number(cents) || 0);
  return '$' + (n % 100 === 0 ? String(n / 100) : (n / 100).toFixed(2));
};

function syncEngravePreview(root) {
  const input = root.querySelector('[data-et-eng-input]');
  const textEl = root.querySelector('[data-et-eng-text]');
  const textPath = textEl ? textEl.querySelector('textPath') : null;
  if (!input || !textPath) return;
  const value = input.value.trim();
  textPath.textContent = value || root.dataset.previewDefault || '';
  textEl.setAttribute('fill-opacity', value ? '0.95' : '0.28');
}

function pdpSet() {
  const form = giftBlockForm();
  // NB: form.id is shadowed by the hidden <input name="id"> (variant) — read the attribute
  const formId = form ? form.getAttribute('id') || '' : '';
  const base = formId
    ? extrasState(formId)
    : { eng: null, engInput: null, engText: '', gbx: null, gbxChecked: false, cov: null, coverWhite: false, active: false };
  const engBlock = document.querySelector('[data-et-eng-block]');
  const engBlockInput = engBlock ? engBlock.querySelector('[data-et-eng-input]') : null;
  const engText = ((engBlockInput && engBlockInput.value.trim()) || base.engText || '').trim();
  const pattern = (engBlockInput && engBlockInput.pattern) || (base.engInput && base.engInput.pattern) || '';
  const engValid = !engText || !pattern || new RegExp(`^(?:${pattern})$`).test(engText);
  const giftBlock = document.querySelector('[data-et-gift-block]');
  const msgInput = giftBlock ? giftBlock.querySelector('[data-et-gift-message]') : null;
  const message = msgInput ? msgInput.value.trim() : '';
  const boxWanted = !!(base.gbxChecked || message);
  return { form, formId, base, engBlock, engBlockInput, engText, engValid, giftBlock, msgInput, message, boxWanted };
}

function renderSet(root, own, set) {
  const withEng = own === 'eng' || (!!set.engText && set.engValid);
  const withBox = own === 'box' || set.boxWanted;
  const racket = Number(root.dataset.racketPrice) || 0;
  const engPrice = Number(root.dataset.engPrice) || 0;
  const boxPrice = Number(root.dataset.boxPrice) || 0;
  const coverPrice = Number(root.dataset.coverPrice) || 0;
  const coverRow = root.querySelector('[data-set-cover]');
  const withCover = !!set.base.coverWhite && !!coverRow;
  if (coverRow) coverRow.hidden = !withCover;
  const engRow = root.querySelector('[data-set-eng]');
  const engTextEl = root.querySelector('[data-set-eng-text]');
  const boxRow = root.querySelector('[data-set-box]');
  const totalEl = root.querySelector('[data-set-total]');
  if (engRow) engRow.hidden = !withEng;
  if (engTextEl) engTextEl.textContent = set.engText && set.engValid ? `“${set.engText}”` : '';
  if (boxRow) boxRow.hidden = !withBox;
  const total = racket + (withEng && engRow ? engPrice : 0) + (withBox && boxRow ? boxPrice : 0) + (withCover ? coverPrice : 0);
  if (totalEl) totalEl.textContent = money(total);
  const btn = root.querySelector('[data-et-set-add]');
  if (btn) {
    const both = withEng && withBox && engRow && boxRow;
    const label = both ? root.dataset.btnBoth : root.dataset.btnBase;
    // 22.10.2026 (test): every button says what the set costs
    const text = label ? `${label} · ${money(total)}` : '';
    if (text && btn.textContent !== text) btn.textContent = text;
  }
  const count = root.querySelector('[data-et-gift-count]');
  if (count && set.msgInput) count.textContent = String(set.msgInput.value.length);
}

function refreshPdpSets() {
  const set = pdpSet();
  if (set.engBlock) renderSet(set.engBlock, 'eng', set);
  if (set.giftBlock) renderSet(set.giftBlock, 'box', set);
  refreshCtaTotals();
}

/* buy-box set total (22.10.2026, test): the main button and the sticky bar add the racket with the
   cover chosen in the buy-box and the engraving / gift box of the buy-box rows — they show that total,
   and the line under the cover choice lists the parts ("Racket $270 + Club White $10"). */
function buyBoxSet() {
  const cov = document.querySelector('[data-et-cover]');
  if (!cov) return null;
  const state = extrasState(cov.dataset.formId);
  const d = cov.dataset;
  const parts = [{ label: d.sumRacket || 'Racket', price: Number(d.racketPrice) || 0 }];
  if (state.coverWhite) parts.push({ label: d.coverWhiteName || 'Club White', price: Number(d.coverPrice) || 0 });
  const pattern = state.engInput ? state.engInput.pattern : '';
  if (state.engText && (!pattern || new RegExp(`^(?:${pattern})$`).test(state.engText))) {
    parts.push({ label: d.sumEngraving || 'Engraving', price: Number(d.engPrice) || 0 });
  }
  if (state.gbxChecked) parts.push({ label: d.sumBox || 'Gift box', price: Number(d.boxPrice) || 0 });
  return { cov, formId: cov.dataset.formId, parts, total: parts.reduce((sum, part) => sum + part.price, 0) };
}

function setButtonTotal(button, total) {
  if (!button) return;
  const host = button.querySelector('.add-to-cart-text__content > span') || button;
  let el = host.querySelector(':scope > [data-et-cta-total]');
  if (button.disabled || !total) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('span');
    el.className = 'et-cta-total';
    el.setAttribute('data-et-cta-total', '');
    host.appendChild(el);
  }
  // non-breaking spaces: the theme lays the label out as a flex item, which drops a leading space
  const text = `\u00a0·\u00a0${money(total)}`;
  if (el.textContent !== text) el.textContent = text;
}

function refreshCtaTotals() {
  const set = buyBoxSet();
  if (!set) return;
  const form = document.getElementById(set.formId);
  setButtonTotal(form ? form.querySelector('button[name="add"]') : null, set.total);
  const extras = set.parts.length > 1;
  const sticky = document.querySelector('.sticky-add-to-cart__price');
  if (sticky) {
    let el = sticky.querySelector(':scope > [data-et-sticky-total]');
    if (extras) {
      if (!el) {
        el = document.createElement('span');
        el.className = 'et-sticky-total';
        el.setAttribute('data-et-sticky-total', '');
        sticky.appendChild(el);
      }
      el.textContent = money(set.total);
      sticky.setAttribute('data-et-total', '');
    } else {
      if (el) el.remove();
      sticky.removeAttribute('data-et-total');
    }
  }
  const sum = set.cov.querySelector('[data-et-cover-sum]');
  if (sum) {
    sum.hidden = !extras;
    if (extras) sum.textContent = set.parts.map((part) => `${part.label} ${money(part.price)}`).join(' + ');
  }
}

/* keep the buyer's choices across Matte ↔ Glossy (22.10.2026, test): the finish switch is a link to
   the other product; the cover, the engraving text, the gift box and its message travel along in
   sessionStorage and are put back on the other page. */
const CARRY_KEY = 'et-pdp-carry';

function pdpProductId() {
  const root = document.querySelector('[data-et-cover], [data-et-eng-block], [data-et-gift-block]');
  return root ? root.dataset.productId || '' : '';
}

document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a.et-finish__opt[href]') : null;
    if (!link || link.hasAttribute('aria-current')) return;
    const set = pdpSet();
    const data = {
      at: Date.now(),
      from: pdpProductId(),
      cover: set.base.coverWhite ? 'white' : 'black',
      eng: set.engText || '',
      box: !!set.base.gbxChecked,
      msg: set.message || '',
    };
    try {
      sessionStorage.setItem(CARRY_KEY, JSON.stringify(data));
    } catch {}
  },
  { capture: true }
);

function restoreCarriedChoices() {
  let data = null;
  try {
    data = JSON.parse(sessionStorage.getItem(CARRY_KEY) || 'null');
    sessionStorage.removeItem(CARRY_KEY);
  } catch {
    return;
  }
  if (!data || Date.now() - Number(data.at) > 10 * 60 * 1000 || data.from === pdpProductId()) return;
  const set = pdpSet();
  if (data.cover === 'white' && set.base.cov) {
    const white = set.base.cov.querySelector('[data-et-cover-input][value="white"]');
    if (white && !white.disabled && !white.checked) {
      white.checked = true;
      white.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  const engInput = set.engBlockInput || set.base.engInput;
  if (data.eng && engInput) {
    engInput.value = data.eng;
    engInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
  const boxInput = set.base.gbx ? set.base.gbx.querySelector('[data-et-gbx-input]') : null;
  if (data.box && boxInput && !boxInput.checked) {
    boxInput.checked = true;
    boxInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (data.msg && set.msgInput) {
    set.msgInput.value = data.msg;
    set.msgInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
  refreshPdpSets();
}

function resetPdpInputs(set) {
  if (set.engBlockInput) {
    set.engBlockInput.value = '';
    if (set.engBlock) syncEngravePreview(set.engBlock);
  }
  if (set.base.engInput && set.base.engInput.value) {
    set.base.engInput.value = '';
    set.base.engInput.dispatchEvent(new Event('input', { bubbles: true }));
  }
  if (set.msgInput) set.msgInput.value = '';
}

let setAddBusy = false;

async function pdpSetAdd(root, own, btn) {
  if (setAddBusy) return;
  setAddBusy = true;
  const errorEl = root.querySelector('[data-et-set-error]');
  const fail = (text) => {
    if (!errorEl) return;
    errorEl.textContent = text;
    errorEl.hidden = false;
  };
  if (errorEl) errorEl.hidden = true;
  btn.disabled = true;
  try {
    const set = pdpSet();
    const wantEng = own === 'eng' || (!!set.engText && set.engValid);
    const wantBox = own === 'box' || set.boxWanted;
    if (own === 'eng' && !set.engText) {
      fail(root.dataset.emptyText || 'Please type the text to engrave.');
      if (set.engBlockInput) set.engBlockInput.focus();
      return;
    }
    if (set.engText && !set.engValid) {
      fail(root.dataset.patternText || 'Latin letters, numerals and simple punctuation, up to 15 characters.');
      if (own === 'eng' && set.engBlockInput) set.engBlockInput.focus();
      return;
    }
    if (!set.form || !set.formId) {
      fail('Could not add to cart. Please use the button at the top of the page.');
      return;
    }
    const formId = set.formId;
    // canonical sources of fee variants and property names: the buy-box rows, else the blocks
    const engRoot = set.base.eng || set.engBlock;
    const gbxRoot = set.base.gbx || set.giftBlock;
    if (wantEng && !(engRoot && engRoot.dataset.feeVariant)) {
      fail('Engraving is not available for this racket.');
      return;
    }
    if (wantBox && !(gbxRoot && gbxRoot.dataset.feeVariant)) {
      fail('The gift box is not available right now.');
      return;
    }
    if (set.engBlock) set.engBlock.dataset.formId = formId;
    if (set.giftBlock) set.giftBlock.dataset.formId = formId;
    const message = wantBox ? set.message : '';
    const messageKey = (set.giftBlock && set.giftBlock.dataset.messageKey) || GIFT_MESSAGE_KEY;

    // the same racket already in the cart → attach the missing extras to that line
    const racketId = Number(new FormData(set.form).get('id')) || 0;
    const cart = await fetchCart();
    const line =
      cart && Array.isArray(cart.items)
        ? cart.items.find((item) => item.id === racketId && !(item.properties && item.properties._service)) || null
        : null;
    if (line) {
      const props = line.properties || {};
      const needEng = wantEng && !props._pers_id;
      const needBox = wantBox && !props._gift_id;
      const needCover = !!set.base.coverWhite && !props._cover_id;
      if (needEng || needBox || needCover) {
        const stamp = Date.now().toString(36);
        const extraProps = {};
        if (needEng) {
          extraProps[engRoot.dataset.propertyKey || 'Engraving — The Crown'] = set.engText;
          extraProps._pers_id = `crown-${line.id}-${stamp}`;
          extraProps._fee_variant = engRoot.dataset.feeVariant;
          extraProps._pers_source = 'pdp-block';
        }
        if (needBox) {
          extraProps[gbxRoot.dataset.propertyKey || GIFT_PROPERTY_KEY] = gbxRoot.dataset.propertyValue || 'Included';
          extraProps._gift_id = `gift-${line.id}-${stamp}`;
          extraProps._gift_variant = gbxRoot.dataset.feeVariant;
          extraProps._gift_source = 'pdp-block';
          if (message) extraProps[messageKey] = message;
        }
        if (needCover) {
          const cov = set.base.cov;
          extraProps[cov.dataset.propertyKey || COVER_PROPERTY_KEY] = cov.dataset.propertyValue || 'Club White';
          extraProps._cover_id = `cover-${line.id}-${stamp}`;
          extraProps._cover_variant = cov.dataset.feeVariant;
        }
        const ok = await changeLineProperties(line, extraProps);
        if (!ok) {
          fail('Could not update your cart. Please try again.');
          return;
        }
        invalidateCartSnapshot();
        await reconcile(); // pairs the fee lines; the trailing pass retries a rejected add
        scheduleReconcile();
        resetPdpInputs(set);
        await announceRetroAdd(formId, root.dataset.productId);
        return;
      }
      // the line already carries everything chosen → a fresh add, as the buy-box would do
    }

    const state = {
      eng: wantEng ? engRoot : null,
      engInput: set.engBlockInput || set.base.engInput,
      engText: wantEng ? set.engText : '',
      gbx: gbxRoot,
      gbxChecked: wantBox,
      cov: set.base.cov,
      coverWhite: !!set.base.coverWhite,
      active: true,
    };
    await batchAdd(set.form, state, { message, messageKey, source: 'pdp-block', errorEl });
    if (errorEl && errorEl.hidden) resetPdpInputs(set);
  } catch (error) {
    console.error('[et-engraving] set add failed', error);
    fail('Could not add to cart. Please try again.');
  } finally {
    btn.disabled = false;
    setAddBusy = false;
    refreshPdpSets();
  }
}

let mirroring = false;
function mirrorEngravingText(from) {
  if (mirroring) return;
  mirroring = true;
  try {
    const value = from.value;
    const set = pdpSet();
    const targets = [];
    if (set.engBlockInput && set.engBlockInput !== from) targets.push(set.engBlockInput);
    if (set.base.engInput && set.base.engInput !== from) targets.push(set.base.engInput);
    for (const target of targets) {
      if (target.value === value) continue;
      target.value = value;
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (set.engBlock) syncEngravePreview(set.engBlock);
  } finally {
    mirroring = false;
  }
}

function initPdpSetBlocks(scope) {
  const roots = (scope || document).querySelectorAll('[data-et-eng-block], [data-et-gift-block]');
  for (const root of roots) {
    if (root.dataset.etSetInit) continue;
    root.dataset.etSetInit = '1';
    const input = root.querySelector('[data-et-eng-input]');
    if (input) {
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const btn = root.querySelector('[data-et-set-add]');
        if (btn && !btn.disabled) pdpSetAdd(root, 'eng', btn);
      });
      syncEngravePreview(root);
    }
  }
  refreshPdpSets();
}

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (target.matches('[data-et-eng-block] [data-et-eng-input], [data-et-engraving] [data-et-eng-input]')) {
    mirrorEngravingText(target);
    refreshPdpSets();
    return;
  }
  if (target.matches('[data-et-gift-message]')) refreshPdpSets();
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement && target.matches('[data-et-gbx-input]')) refreshPdpSets();
});

// "Remove" next to a chosen extra in a block's summary: drops that choice everywhere on the page
function pdpSetRemove(what) {
  const set = pdpSet();
  if (what === 'eng') {
    for (const input of [set.engBlockInput, set.base.engInput]) {
      if (!input || !input.value) continue;
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (set.engBlock) syncEngravePreview(set.engBlock);
  } else if (what === 'box') {
    if (set.msgInput && set.msgInput.value) {
      set.msgInput.value = '';
      set.msgInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const gbxInput = set.base.gbx ? set.base.gbx.querySelector('[data-et-gbx-input]') : null;
    if (gbxInput && gbxInput.checked) {
      gbxInput.checked = false;
      gbxInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  } else if (what === 'cover') {
    const black = set.base.cov ? set.base.cov.querySelector('[data-et-cover-input][value="black"]') : null;
    if (black && !black.checked) {
      black.checked = true;
      black.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  refreshPdpSets();
}

document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-set-remove]') : null;
  if (!btn) return;
  event.preventDefault();
  pdpSetRemove(btn.getAttribute('data-set-remove'));
});

document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-et-set-add]') : null;
  if (!btn) return;
  const root = btn.closest('[data-et-eng-block], [data-et-gift-block]');
  if (!root) return;
  event.preventDefault();
  pdpSetAdd(root, btn.dataset.own === 'box' ? 'box' : 'eng', btn);
});

/* ---------------- cover: Classic Black or Club White (21.10.2026, test theme "combined") ---------------- */
// Classic Black comes with every racket and leaves no trace in the cart. Club White is the hidden
// product the-white-cover, paired with its racket line via `_cover_id` (guardian: SERVICES 'cover').
// Two places to choose: the buy-box block (blocks/et-cover.liquid, radios) and the cart under each
// racket line (snippets/et-cover-cart.liquid). Both open the same large view (cover lightbox).

// Put Club White on a racket line already in the cart. The racket is re-added together with its
// cover in one request, racket last: the cart lists the newest line first, so the racket returns
// to the top with its cover right below it. On failure the racket is put back as it was.
async function attachCoverToLine(line, feeVariant, propertyKey, propertyValue) {
  const props = { ...(line.properties || {}) };
  const coverId = `cover-${line.id}-${Date.now().toString(36)}`;
  props[propertyKey || COVER_PROPERTY_KEY] = propertyValue || 'Club White';
  props._cover_id = coverId;
  props._cover_variant = String(feeVariant);
  const removed = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ id: line.key, quantity: 0 }),
  });
  if (!removed.ok) return false;
  const added = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      items: [
        {
          id: Number(feeVariant),
          quantity: line.quantity,
          properties: { For: line.product_title || '', _service: 'cover', _cover_id: coverId },
        },
        { id: line.id, quantity: line.quantity, properties: props },
      ],
    }),
  });
  const data = added.ok ? await added.json().catch(() => null) : null;
  if (added.ok && !(data && data.status)) return true;
  await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ items: [{ id: line.id, quantity: line.quantity, properties: line.properties || {} }] }),
  });
  return false;
}

// Back to Classic Black. cart/change ignores an empty properties object, so the cover marks cannot
// be dropped in place: the racket is re-added without them and the guardian drops the orphaned
// Club White line.
async function detachCoverFromLine(line, propertyKey) {
  const props = { ...(line.properties || {}) };
  delete props[propertyKey || COVER_PROPERTY_KEY];
  delete props._cover_id;
  delete props._cover_variant;
  const removed = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ id: line.key, quantity: 0 }),
  });
  if (!removed.ok) return false;
  const added = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ items: [{ id: line.id, quantity: line.quantity, properties: props }] }),
  });
  if (added.ok) return true;
  await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ items: [{ id: line.id, quantity: line.quantity, properties: line.properties || {} }] }),
  });
  return false;
}

let coverSwitchBusy = false;

async function switchCover(root, want) {
  if (coverSwitchBusy || root.dataset.state === want) return;
  coverSwitchBusy = true;
  setCartBusy(true);
  root.classList.add('is-busy');
  try {
    const cart = await fetchCart();
    const line = cart && Array.isArray(cart.items) ? cart.items.find((item) => item.key === root.dataset.lineKey) : null;
    if (!line) return;
    const hasCover = !!(line.properties && line.properties._cover_id);
    if (want === 'white' && !hasCover) {
      if (!(await attachCoverToLine(line, root.dataset.feeVariant, root.dataset.propertyKey, root.dataset.propertyValue))) {
        throw new Error('cover attach failed');
      }
    } else if (want === 'black' && hasCover) {
      if (!(await detachCoverFromLine(line, root.dataset.propertyKey))) throw new Error('cover detach failed');
    } else {
      return;
    }
    invalidateCartSnapshot();
    await reconcile();
  } catch (error) {
    console.error('[et-engraving] cover switch failed', error);
  } finally {
    const cart = await fetchCart();
    if (cart) {
      document.dispatchEvent(
        new CartUpdateEvent(cart, GUARDIAN_SOURCE, { source: GUARDIAN_SOURCE, itemCount: cart.item_count })
      );
    }
    coverSwitchBusy = false;
    settleCartBusy();
  }
}

/* cover large view — one <dialog> for the page, filled from the data-cover-* attributes of the
   chooser that opened it; its button applies the choice to that chooser (buy-box or cart line) */

const COVER_LIGHTBOX_CSS = `
.et-clb { width: min(100vw - 2rem, 58rem); max-width: none; max-height: calc(100dvh - 2rem); padding: 0; border: 0; border-radius: 2px; background: #F0EBDC; color: #2C4A33; overflow: auto; }
.et-clb::backdrop { background: rgba(18, 28, 21, 0.62); }
.et-clb__panel { position: relative; display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); align-items: center; }
/* the photo always fits whole: the width follows the height cap instead of the image being cropped (22.10.2026) */
.et-clb__media { display: flex; justify-content: center; background: #E4DCCB; }
.et-clb__img { display: block; width: auto; max-width: 100%; height: auto; max-height: calc(100dvh - 2rem); }
.et-clb__info { padding: 2.6rem 2.4rem; }
.et-clb__kicker { margin: 0 0 1rem; font-family: var(--font-subheading--family, 'Inter', sans-serif); font-size: 0.68rem; letter-spacing: 0.16em; text-transform: uppercase; opacity: 0.8; }
.et-clb__switch { display: inline-flex; margin: 0 0 1.6rem; border: 1px solid rgba(44, 74, 51, 0.35); border-radius: 2px; overflow: hidden; }
.et-clb__tab { padding: 0.55rem 0.9rem; border: 0; background: transparent; color: #4A4438; font-family: var(--font-subheading--family, 'Inter', sans-serif); font-size: 0.66rem; letter-spacing: 0.12em; text-transform: uppercase; line-height: 1; cursor: pointer; }
.et-clb__tab + .et-clb__tab { border-left: 1px solid rgba(44, 74, 51, 0.35); }
.et-clb__tab[aria-selected='true'] { background: #2C4A33; color: #F0E8D6; cursor: default; }
.et-clb__name { margin: 0; font-family: var(--font-heading--family, 'Playfair Display', Georgia, serif); font-size: clamp(1.8rem, 3vw, 2.4rem); font-weight: 400; line-height: 1.1; }
.et-clb__note { margin: 0.5rem 0 0; font-family: var(--font-body--family, 'Cardo', Georgia, serif); font-size: 1.1rem; opacity: 0.75; font-variant-numeric: tabular-nums; }
.et-clb__text { margin: 1.2rem 0 0; font-family: var(--font-body--family, 'Cardo', Georgia, serif); font-size: 1.05rem; line-height: 1.6; opacity: 0.88; }
.et-clb__cta { display: block; width: 100%; margin: 2rem 0 0; padding: 1rem 1.2rem; border: 1px solid #2C4A33; border-radius: 2px; background: #2C4A33; color: #F0E8D6; font-family: var(--font-subheading--family, 'Inter', sans-serif); font-size: 0.72rem; letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer; }
.et-clb__cta:hover { background: #22392A; }
.et-clb__cta[disabled] { background: transparent; color: #2C4A33; cursor: default; opacity: 0.75; }
.et-clb__close { position: absolute; top: 0.6rem; right: 0.6rem; z-index: 1; display: grid; place-items: center; width: 2.4rem; height: 2.4rem; border: 0; border-radius: 50%; background: rgba(240, 235, 220, 0.9); color: #2C4A33; font-size: 1.5rem; line-height: 1; cursor: pointer; }
.et-clb__close:focus-visible, .et-clb__tab:focus-visible, .et-clb__cta:focus-visible { outline: 2px solid #2C4A33; outline-offset: 2px; }
@media screen and (max-width: 749px) {
  .et-clb { width: 100vw; max-height: 100dvh; height: 100dvh; margin: 0; border-radius: 0; }
  .et-clb__panel { grid-template-columns: 1fr; align-items: start; }
  .et-clb__media { background: transparent; padding-top: 0.75rem; }
  .et-clb__img { max-height: 58dvh; border-radius: 2px; }
  .et-clb__info { padding: 1.4rem 1.25rem 1.8rem; }
  .et-clb__switch { margin-bottom: 1.1rem; }
  .et-clb__cta { margin-top: 1.4rem; }
}
/* short phones (iPhone SE): a smaller photo keeps the choose button above the fold */
@media screen and (max-width: 749px) and (max-height: 700px) {
  .et-clb__img { max-height: 46dvh; }
}
`;

let coverLightbox = null;

function coverLightboxEl() {
  if (coverLightbox && coverLightbox.dialog.isConnected) return coverLightbox;
  const style = document.createElement('style');
  style.textContent = COVER_LIGHTBOX_CSS;
  document.head.appendChild(style);
  const dialog = document.createElement('dialog');
  dialog.className = 'et-clb';
  dialog.innerHTML = `
    <div class="et-clb__panel">
      <button type="button" class="et-clb__close" data-clb-close aria-label="Close">&times;</button>
      <div class="et-clb__media"><img class="et-clb__img" data-clb-img alt="" width="900" height="1200"></div>
      <div class="et-clb__info">
        <p class="et-clb__kicker" data-clb-kicker></p>
        <div class="et-clb__switch" role="tablist">
          <button type="button" class="et-clb__tab" role="tab" data-clb-tab="black"></button>
          <button type="button" class="et-clb__tab" role="tab" data-clb-tab="white"></button>
        </div>
        <h2 class="et-clb__name" data-clb-name></h2>
        <p class="et-clb__note" data-clb-note></p>
        <p class="et-clb__text" data-clb-text></p>
        <button type="button" class="et-clb__cta" data-clb-cta></button>
      </div>
    </div>`;
  document.body.appendChild(dialog);
  const state = { dialog, root: null, which: 'white' };
  dialog.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    // a click on the backdrop lands on the dialog element itself
    if (target === dialog || target.closest('[data-clb-close]')) {
      dialog.close();
      return;
    }
    const tab = target.closest('[data-clb-tab]');
    if (tab) {
      renderCoverLightbox(tab.getAttribute('data-clb-tab'));
      return;
    }
    if (target.closest('[data-clb-cta]') && state.root) {
      applyCoverChoice(state.root, state.which);
      dialog.close();
    }
  });
  coverLightbox = state;
  return state;
}

function currentCoverChoice(root) {
  if (root.hasAttribute('data-et-cover-cart')) return root.dataset.state === 'white' ? 'white' : 'black';
  const checked = root.querySelector('[data-et-cover-input]:checked');
  return checked && checked.value === 'white' ? 'white' : 'black';
}

function renderCoverLightbox(which) {
  const lb = coverLightboxEl();
  const root = lb.root;
  if (!root) return;
  lb.which = which;
  const d = root.dataset;
  const pick = (key) => (which === 'white' ? d[`coverWhite${key}`] : d[`coverBlack${key}`]) || '';
  const q = (sel) => lb.dialog.querySelector(sel);
  const img = q('[data-clb-img]');
  img.src = pick('Img');
  img.alt = pick('Name');
  q('[data-clb-kicker]').textContent = d.coverHeading || 'Cover';
  q('[data-clb-close]').setAttribute('aria-label', d.coverClose || 'Close');
  q('[data-clb-name]').textContent = pick('Name');
  q('[data-clb-note]').textContent = pick('Note');
  q('[data-clb-text]').textContent = pick('Text');
  for (const tab of lb.dialog.querySelectorAll('[data-clb-tab]')) {
    const own = tab.getAttribute('data-clb-tab');
    tab.textContent = own === 'white' ? d.coverWhiteName || 'Club White' : d.coverBlackName || 'Classic Black';
    tab.setAttribute('aria-selected', String(own === which));
  }
  const cta = q('[data-clb-cta]');
  const chosen = currentCoverChoice(root) === which;
  cta.disabled = chosen;
  cta.textContent = chosen
    ? d.coverSelected || 'Your choice'
    : `${d.coverChoose || 'Choose'} ${pick('Name')}${which === 'white' && pick('Note') ? ` · ${pick('Note')}` : ''}`;
  // warm the other photo so switching tabs is instant
  const other = new Image();
  other.src = which === 'white' ? d.coverBlackImg || '' : d.coverWhiteImg || '';
}

function openCoverLightbox(root, which) {
  const lb = coverLightboxEl();
  lb.root = root;
  renderCoverLightbox(which === 'black' ? 'black' : 'white');
  if (!lb.dialog.open) lb.dialog.showModal();
  const close = lb.dialog.querySelector('[data-clb-close]');
  if (close) close.focus();
}

function applyCoverChoice(root, which) {
  if (root.hasAttribute('data-et-cover-cart')) {
    switchCover(root, which);
    return;
  }
  const input = root.querySelector(`[data-et-cover-input][value="${which}"]`);
  if (input && !input.disabled && !input.checked) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const zoom = target.closest('[data-et-cover-zoom]');
  if (zoom) {
    const root = zoom.closest('[data-et-cover], [data-et-cover-cart]');
    if (!root) return;
    event.preventDefault();
    openCoverLightbox(root, zoom.getAttribute('data-et-cover-zoom'));
    return;
  }
  const btn = target.closest('[data-et-cover-pick]');
  if (!btn) return;
  const root = btn.closest('[data-et-cover-cart]');
  if (!root) return;
  event.preventDefault();
  switchCover(root, btn.dataset.etCoverPick === 'white' ? 'white' : 'black');
});

/* ---------------- boot ---------------- */

initBlocks(document);
initPdpSetBlocks(document);
restoreCarriedChoices();
document.addEventListener('shopify:section:load', (event) => {
  initBlocks(event.target);
  initPdpSetBlocks(event.target);
});
setTimeout(scheduleReconcile, 1500);
